import { AppointmentTimeZone } from "@/api/enums/appointment.enums";

const UTC = "UTC" as const;
const LOCALE = "en-US" as const;

type DateInput = Date | string | number;

const toDate = (value: DateInput): Date =>
  value instanceof Date ? value : new Date(value);

const appointmentTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: UTC,
});

const utcTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: UTC,
  timeZoneName: "short",
});

const displayDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: UTC,
});

const longDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: UTC,
});

const monthDayFormatter = new Intl.DateTimeFormat(LOCALE, {
  month: "short",
  day: "numeric",
  timeZone: UTC,
});

const weekdayLongFormatter = new Intl.DateTimeFormat(LOCALE, {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: UTC,
});

const weekdayShortFormatter = new Intl.DateTimeFormat(LOCALE, {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: UTC,
});

export function formatDateKey(value: DateInput): string {
  const d = toDate(value);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Appointment values are Eastern wall-clock times serialized by the API on
 * the UTC axis. Keeping that axis preserves the time shown in API emails;
 * the explicit label prevents the browser's local zone from being implied.
 */
export function formatAppointmentTime(
  value: DateInput,
  timeZoneLabel: string = AppointmentTimeZone.EST,
): string {
  return `${appointmentTimeFormatter.format(toDate(value))} ${timeZoneLabel}`;
}

export function formatUtcTime(value: DateInput): string {
  return utcTimeFormatter.format(toDate(value));
}

export function formatDisplayDate(value: DateInput): string {
  return displayDateFormatter.format(toDate(value));
}

export function formatLongDate(value: DateInput): string {
  return longDateFormatter.format(toDate(value));
}

export function formatMonthDay(value: DateInput): string {
  return monthDayFormatter.format(toDate(value));
}

export function formatWeekdayLong(value: DateInput): string {
  return weekdayLongFormatter.format(toDate(value));
}

export function formatWeekdayShort(value: DateInput): string {
  return weekdayShortFormatter.format(toDate(value));
}
